import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  ColorPicker,
  Divider,
  Flex,
  Input,
  Modal,
  Space,
  Tag,
} from "antd";
import React, { useState } from "react";
import { TbTrash } from "react-icons/tb";
import { useToken } from "../../hooks/useToken";
import { TInterest } from "../../models/interests.model";
import { useTheme } from "../../providers/ThemeProvider";
import {
  createInterest,
  deleteInterest,
} from "../../services/interests.service";
import { hexToRgba } from "../../utils/hexToRgba";

interface CategoryDropdownContentProps {
  allCategories: TInterest[];
  selectedCategories: string[];
  onChange: (selectedIds: string[]) => void;
  onAddCategory: (interest: TInterest) => void;
  limit?: number;
}

export const CategoryDropdownContent: React.FC<
  CategoryDropdownContentProps
> = ({ allCategories, selectedCategories, onChange, onAddCategory, limit }) => {
  const { message } = useTheme();
  const [categoryDelete, setCategoryDelete] = useState<TInterest>();
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#000000");
  const [isDeletingCategory, setDeletingCategory] = useState(false);
  const { token } = useToken();

  const handleNewCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewCategoryName(e.target.value);
  };

  const handleAddNewCategory = () => {
    const trimmedName = newCategoryName.trim();

    if (!trimmedName) {
      message.error("Category name cannot be empty.");
      return;
    }

    const exists = allCategories.some(
      (category) => category.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (exists) {
      message.warning(`Category "${trimmedName}" already exists.`);
      return;
    }

    createInterest({
      name: trimmedName,
      color: newCategoryColor,
      onSuccess: (newCategory) => {
        onChange([...selectedCategories, newCategory.id]);
        onAddCategory(newCategory);
        message.success(`Category "${trimmedName}" added successfully.`);
      },
      onError: (error) => {
        message.error(`Error adding category "${trimmedName}": ${error}`);
      },
    });

    setNewCategoryName("");
  };

  const handleTagCheckChange = (categoryId: string, checked: boolean) => {
    const nextSelectedIds = checked
      ? [...selectedCategories, categoryId]
      : selectedCategories.filter((id) => id !== categoryId);
    onChange(nextSelectedIds);
  };

  const limitReached = limit ? selectedCategories.length >= limit : false;

  return (
    <>
      <Modal
        title="Confirm Deletion"
        open={categoryDelete != undefined}
        onOk={() => {
          setDeletingCategory(true);

          deleteInterest({
            id: categoryDelete?.id || "",
            onSuccess: () => {
              setDeletingCategory(false);
              message.success(
                `Category "${categoryDelete?.name}" deleted successfully.`
              );
              onChange(
                selectedCategories.filter((id) => id !== categoryDelete?.id)
              );
              setCategoryDelete(undefined);

              PubSub.publish("categoryDeleted", categoryDelete);
            },
            onError: (error) => {
              setDeletingCategory(false);
              message.error(`Error deleting category: ${error}`);
            },
          });
        }}
        confirmLoading={isDeletingCategory}
        onCancel={() => setCategoryDelete(undefined)}
        okText="Delete"
        okType="danger"
        centered
      >
        <p>Are you sure you want to permanently delete this category?</p>
        {categoryDelete?.name && (
          <p>
            <strong>{categoryDelete.name}</strong>
          </p>
        )}
      </Modal>

      <div
        style={{
          padding: "12px",
          minWidth: "300px",
          width: "100%",
        }}
      >
        <Space
          wrap
          size={[12, 12]}
          style={{
            width: "100%",
          }}
        >
          {allCategories.map((category) => {
            const isSelected = selectedCategories.includes(category.id);
            const isDisabled = !isSelected && limitReached;
            return (
              <Tag
                key={category.id}
                onClick={() => {
                  if (!isDisabled) {
                    handleTagCheckChange(category.id, !isSelected);
                  }
                }}
                style={{
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  fontSize: token?.fontSize || "14px",
                  borderRadius: "4px",
                  border: `1px solid ${category.color}`,
                  color: category.color,
                  opacity: isDisabled ? 0.4 : isSelected ? 1 : 0.4,
                  transition: "opacity 0.3s ease, background-color 0.3s ease",
                  backgroundColor:
                    hexToRgba(category.color, 0.2) || category.color,
                }}
                onClose={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setCategoryDelete(category);
                }}
                closeIcon={<TbTrash style={{ fill: category.color }} />}
                closable={!isDisabled && category.public == false}
              >
                {category.name}
              </Tag>
            );
          })}
        </Space>

        <Divider style={{ margin: "12px 0" }} />

        <Flex style={{ width: "100%" }}>
          <Input
            placeholder="Add new category..."
            value={newCategoryName}
            onChange={handleNewCategoryChange}
            onPressEnter={handleAddNewCategory}
          />
          <ColorPicker
            disabledAlpha
            disabledFormat
            value={newCategoryColor}
            onChange={(color) => {
              setNewCategoryColor(color.toHex());
            }}
            style={{
              backgroundColor: "transparent",
              border: "none",
              marginLeft: token.marginSM,
            }}
          />
          <Button
            icon={<PlusOutlined />}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
              color: token?.colorPrimary || "#1890ff",
            }}
            onClick={handleAddNewCategory}
          >
            Add
          </Button>
        </Flex>
      </div>
    </>
  );
};
